import { useEffect, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check } from 'lucide-react';

import type { Configuracao } from '@/api/secretario';
import { Button } from '@/components/ui/button';
import { CollapsibleCard } from '@/components/ui/CollapsibleCard';
import { Spinner } from '@/components/ui/spinner';
import { useNotification } from '@/context/NotificationProvider';
import { notifyMutationError } from '@/lib/errors';
import { queryKeys } from '@/lib/pwa/queryConfig';

import { useAdvogadoEmEdicao, useGravarPadroes } from './advogadoEmEdicao';
import { HonorariosCampos, temPreco, useHonorariosForm } from './HonorariosCampos';
import { useWizardSave } from './WizardSaveContext';

interface HonorariosGeraisCardProps {
  config: Configuracao;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Chamado ao salvar pelo botão (o pai colapsa e abre a próxima seção). */
  onSaved?: () => void;
}

/**
 * Honorários GERAIS do advogado: valem para toda área sem honorários próprios.
 * Um preço só, em vez de um por área — era configurar cada área que fazia o
 * cadastro do Secretário morrer no meio.
 */
export function HonorariosGeraisCard({ config, open, onOpenChange, onSaved }: HonorariosGeraisCardProps) {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotification();
  const form = useHonorariosForm(config.honorarios);
  const gravar = useGravarPadroes();
  const advogado = useAdvogadoEmEdicao();

  const mutation = useMutation({
    mutationFn: () => gravar({ honorarios: form.paraSalvar() }),
    onSuccess: () => {
      form.marcarSalvo();
      queryClient.invalidateQueries({ queryKey: queryKeys.secretario.config });
      showSuccess('Honorários salvos.');
    },
    onError: (error) => notifyMutationError(error, showError),
  });

  // Auto-save ao navegar (nunca perder o que foi digitado) — salvo depois de "usar os do
  // escritório": o que estava digitado não volta a ser próprio por cima da herança.
  const registerFlush = useWizardSave();
  const { mutate, isPending } = mutation;
  const herdou = useRef(false);
  useEffect(() => {
    registerFlush(
      'honorarios-gerais',
      form.alterado && !isPending ? () => !herdou.current && mutate() : null,
    );
    return () => registerFlush('honorarios-gerais', null);
  }, [form.alterado, isPending, mutate, registerFlush]);

  // Volta a herdar os do escritório: o perfil fica sem honorários próprios (``null``).
  const herdar = useMutation({
    mutationFn: () => {
      herdou.current = true;
      return gravar({ honorarios: null });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.secretario.config });
      showSuccess('Voltaram a valer os honorários do escritório.');
    },
    onError: (error) => notifyMutationError(error, showError),
  });

  // Resumo do estado SALVO, não do que está sendo digitado. No escritório o padrão é
  // opcional (sem ele, cada advogado usa os próprios): falta dele não é alerta.
  const doEscritorio = !advogado && config.escopo === 'ESCRITORIO';
  const definido = temPreco(config.honorarios);
  const summary = advogado?.herdaHonorarios && definido ? (
    <span className="text-muted-foreground">Usa os do escritório</span>
  ) : definido ? (
    <span className="flex items-center gap-1 text-emerald-600">
      <Check size={14} /> {advogado ? 'Próprios' : 'Definidos'}
    </span>
  ) : doEscritorio ? (
    <span className="text-muted-foreground">Não definidos</span>
  ) : (
    <span className="text-amber-600">Informe os honorários</span>
  );

  return (
    <CollapsibleCard
      title={advogado ? 'Honorários' : doEscritorio ? 'Honorários do escritório' : 'Seus honorários'}
      summary={summary}
      open={open}
      onOpenChange={onOpenChange}
    >
      <p className="text-sm text-muted-foreground">
        {advogado?.herdaHonorarios
          ? 'Hoje valem os do escritório. Mudar aqui cria os honorários próprios deste advogado. '
          : doEscritorio
            ? 'Valem para o advogado sem honorários próprios. '
            : ''}
        Área com preço diferente define o seu na própria área. O Secretário informa exatamente o que estiver aqui,
        sem negociar.
      </p>

      <HonorariosCampos form={form} idPrefixo="hon-gerais" />

      <div className="flex flex-wrap justify-end gap-2">
        {advogado && !advogado.herdaHonorarios && advogado.escritorioTemHonorarios && (
          <Button
            type="button"
            variant="ghost"
            className="min-h-11"
            onClick={() => herdar.mutate()}
            disabled={herdar.isPending}
          >
            {herdar.isPending && <Spinner size={16} className="mr-2 text-inherit" />}
            Usar os do escritório
          </Button>
        )}
        <Button
          type="button"
          variant="outline"
          className="min-h-11"
          onClick={() => mutation.mutate(undefined, { onSuccess: () => onSaved?.() })}
          disabled={mutation.isPending || (!!advogado && !form.alterado)}
        >
          {mutation.isPending && <Spinner size={16} className="mr-2 text-inherit" />}
          Salvar honorários
        </Button>
      </div>
    </CollapsibleCard>
  );
}
